/**
 * Wheelchair assistance questionnaire (founder ask, 2026-10-05).
 *
 * When a traveler's special request mentions a wheelchair, the airline needs
 * four Yes/No answers before it can arrange help (the founder had to email
 * Lisa, QICM5H, for exactly these). So the questions appear at checkout as
 * soon as the request says "wheelchair", and on the trip afterwards, where
 * the customer can change them at any time and admins can see them. A
 * change the customer makes emails support@.
 *
 * Stored per passenger as `passengers.wheelchair_assistance` (jsonb):
 *   { can_walk_to_aircraft_door, needs_help_on_stairs, needs_lift_to_seat,
 *     has_personal_wheelchair,       // true | false | null (not answered)
 *     updated_at, updated_by }       // ISO string, 'customer' | 'admin'
 *
 * Pure and dependency-free: the checkout, the trip page, the Express API,
 * the emails and the edge functions that insert passengers all read the
 * questions and answers through this one file.
 */

/** The airline's own wording, in the airline's order. */
export const WHEELCHAIR_QUESTIONS = Object.freeze([
  {
    key: 'can_walk_to_aircraft_door',
    label: 'Can you walk from the ticket counter to the aircraft door?',
    short: 'Walks to the aircraft door',
  },
  {
    key: 'needs_help_on_stairs',
    label: 'Do you need help on stairs while you’re boarding the plane?',
    short: 'Help on stairs',
  },
  {
    key: 'needs_lift_to_seat',
    label: 'Do you need to be lifted and transferred onto your seat on the plane?',
    short: 'Lifted onto the seat',
  },
  {
    key: 'has_personal_wheelchair',
    label: 'Are you traveling with your personal wheelchair?',
    short: 'Own wheelchair',
    note: 'Non-collapsible wheelchairs, scooters or other battery-powered wheelchairs can be checked at the ticket counter or departure gate. Keep in mind, some types of lithium-ion batteries could have restrictions.',
  },
]);

export const WHEELCHAIR_KEYS = Object.freeze(WHEELCHAIR_QUESTIONS.map((q) => q.key));

/** "wheelchair", "wheel chair", "wheel-chair", "wheelchairs" or the airline
 *  code WCHR, in any case. A mention is enough: the first question already
 *  covers a traveler who can walk to the door. */
const WHEELCHAIR_PATTERN = /\bwheel[\s-]*chairs?\b|\bwchr\b/i;

export function mentionsWheelchair(text) {
  return typeof text === 'string' && WHEELCHAIR_PATTERN.test(text);
}

/** A passenger's special request, whichever spelling the row carries. */
export function passengerSpecialRequest(p) {
  const v = p?.special_requests ?? p?.specialRequests ?? p?.special_request ?? p?.specialRequest ?? '';
  return typeof v === 'string' ? v : '';
}

function toAnswer(v) {
  if (v === true || v === 'yes' || v === 'Yes' || v === 'true') return true;
  if (v === false || v === 'no' || v === 'No' || v === 'false') return false;
  return null;
}

/** Only the four answers, each true / false / null. Anything else is dropped. */
export function normalizeWheelchairAnswers(input) {
  const src = input && typeof input === 'object' ? input : {};
  const out = {};
  for (const key of WHEELCHAIR_KEYS) out[key] = toAnswer(src[key]);
  return out;
}

export function wheelchairAnswered(answers) {
  const a = normalizeWheelchairAnswers(answers);
  return WHEELCHAIR_KEYS.filter((k) => a[k] !== null).length;
}

export function wheelchairComplete(answers) {
  return wheelchairAnswered(answers) === WHEELCHAIR_KEYS.length;
}

/** Whether this passenger gets the questionnaire: their request mentions a
 *  wheelchair, or answers are already on file (keeps them visible if the
 *  request text is later reworded). */
export function needsWheelchairQuestions(p) {
  return mentionsWheelchair(passengerSpecialRequest(p))
    || wheelchairAnswered(p?.wheelchair_assistance ?? p?.wheelchairAssistance) > 0;
}

export function formatWheelchairAnswer(v) {
  return v === true ? 'Yes' : v === false ? 'No' : 'Not answered';
}

/**
 * The stored record: the four answers, plus when and by whom they were last
 * set. `updatedBy` is 'customer' or 'admin'.
 */
export function wheelchairAssistanceRecord(answers, { updatedBy = 'customer', now = new Date() } = {}) {
  return {
    ...normalizeWheelchairAnswers(answers),
    updated_at: now.toISOString(),
    updated_by: updatedBy === 'admin' ? 'admin' : 'customer',
  };
}

/**
 * The `passengers.wheelchair_assistance` value for a booking insert, from a
 * checkout passenger. null unless the special request still mentions a
 * wheelchair (a customer who typed it, answered, then deleted it changed
 * their mind) and at least one question was answered.
 */
export function wheelchairAssistanceForInsert(p, { now = new Date() } = {}) {
  const raw = p?.wheelchair_assistance ?? p?.wheelchairAssistance;
  if (!raw || typeof raw !== 'object') return null;
  if (!mentionsWheelchair(passengerSpecialRequest(p))) return null;
  if (wheelchairAnswered(raw) === 0) return null;
  return wheelchairAssistanceRecord(raw, { updatedBy: 'customer', now });
}

/** One line for emails: "Walks to the aircraft door: No · Help on stairs:
 *  Yes · …", or '' when nothing is answered. */
export function wheelchairSummary(answers) {
  const a = normalizeWheelchairAnswers(answers);
  if (!WHEELCHAIR_KEYS.some((k) => a[k] !== null)) return '';
  return WHEELCHAIR_QUESTIONS.map((q) => `${q.short}: ${formatWheelchairAnswer(a[q.key])}`).join(' · ');
}

/** The questions whose answer changed, for the support@ email. */
export function diffWheelchairAnswers(before, after) {
  const b = normalizeWheelchairAnswers(before);
  const a = normalizeWheelchairAnswers(after);
  return WHEELCHAIR_QUESTIONS
    .filter((q) => b[q.key] !== a[q.key])
    .map((q) => ({
      key: q.key,
      label: q.label,
      before: formatWheelchairAnswer(b[q.key]),
      after: formatWheelchairAnswer(a[q.key]),
    }));
}
